import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  fetchMessageModerationOffences,
  fetchReportedConversations,
  OFFENCE_STATUSES,
  REPORT_REASONS,
  REPORT_STATUSES,
  updateMessageModerationOffenceStatus,
  updateReportedConversationStatus
} from '../../services/reportedConversationService';

const PageContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
  margin-bottom: var(--space-lg);
  padding-bottom: var(--space-md);
  border-bottom: 1px solid var(--color-border);
`;

const Title = styled.h1`
  font-size: var(--font-size-3xl);
  font-family: var(--font-serif);
  font-weight: var(--font-weight-semibold);
  color: var(--color-primary);
  margin: 0;
`;

const Subtitle = styled.p`
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  margin: 0;
`;

const SummaryRow = styled.div`
  display: flex;
  gap: var(--space-sm);
  align-items: center;
  flex-wrap: wrap;
`;

const SummaryBadge = styled.span`
  background: var(--color-surface);
  color: var(--color-accent);
  border: 1px solid var(--color-border);
  border-radius: 999px;
  padding: var(--space-xs) var(--space-sm);
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-sm);
`;

const ControlsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-sm);
  align-items: center;
  justify-content: space-between;
`;

const FiltersGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-sm);
  align-items: center;
`;

const SearchInput = styled.input`
  height: 46px;
  padding: var(--space-xs) var(--space-sm);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  font-size: var(--font-size-sm);
  min-width: 280px;
  background: var(--color-surface);
  color: var(--color-text);

  &:focus {
    outline: none;
    border-color: var(--color-accent);
    box-shadow: 0 0 0 4px rgba(0, 24, 206, 0.08);
  }
`;

const SelectWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;

  &::after {
    content: '';
    position: absolute;
    top: calc(50% - 1px);
    right: var(--space-sm);
    width: 8px;
    height: 8px;
    border-right: 2px solid var(--color-text-secondary);
    border-bottom: 2px solid var(--color-text-secondary);
    transform: translateY(-50%) rotate(45deg);
    pointer-events: none;
  }
`;

const StatusSelect = styled.select`
  appearance: none;
  height: 46px;
  padding: var(--space-xs) var(--space-xl) var(--space-xs) var(--space-sm);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  font-size: var(--font-size-sm);
  background: white;
  color: var(--color-text);
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--color-accent);
    box-shadow: 0 0 0 4px rgba(0, 24, 206, 0.08);
  }
`;

const Button = styled.button`
  border: 1px solid var(--color-border);
  background: white;
  color: var(--color-text-secondary);
  padding: var(--space-xs) var(--space-sm);
  border-radius: var(--border-radius-md);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;

  &:hover {
    border-color: var(--color-accent);
    color: var(--color-primary);
  }
`;

const TableWrapper = styled.div`
  background: white;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  overflow: hidden;
  overflow-x: auto;
`;

const ReportsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeadCell = styled.th`
  text-align: left;
  padding: var(--space-sm) var(--space-md);
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  background: var(--color-primary-lighter);
  border-bottom: 1px solid var(--color-border);
  white-space: nowrap;
`;

const TableRow = styled.tr`
  &:nth-child(even) {
    background: rgba(247, 247, 243, 0.72);
  }

  &:hover {
    background: rgba(130, 172, 216, 0.08);
  }
`;

const TableCell = styled.td`
  padding: var(--space-md);
  font-size: var(--font-size-md);
  color: var(--color-text);
  border-bottom: 1px solid var(--color-border-light);
  vertical-align: top;
`;

const ReportButton = styled.button`
  background: none;
  border: 0;
  color: var(--color-primary);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-medium);
  text-align: left;
  padding: 0;
  cursor: pointer;
  max-width: 420px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    color: var(--color-accent);
  }
`;

const MetaText = styled.div`
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 86px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  text-transform: capitalize;
  background: ${props => {
    switch (props.status) {
      case 'open': return 'rgba(180, 35, 24, 0.1)';
      case 'reviewing': return 'rgba(180, 83, 9, 0.12)';
      case 'resolved': return 'rgba(4, 120, 87, 0.1)';
      case 'dismissed': return 'rgba(93, 64, 34, 0.08)';
      default: return 'rgba(93, 64, 34, 0.08)';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'open': return 'var(--color-error)';
      case 'reviewing': return 'var(--color-warning-dark)';
      case 'resolved': return 'var(--color-success)';
      default: return 'var(--color-text-muted)';
    }
  }};
`;

const EmptyState = styled.div`
  padding: var(--space-xl);
  text-align: center;
  color: var(--color-text-secondary);
  font-size: var(--font-size-md);
`;

const ErrorBox = styled.div`
  padding: var(--space-lg);
  border: 1px solid var(--color-error);
  border-radius: var(--border-radius-md);
  background: rgba(180, 35, 24, 0.08);
  color: var(--color-error);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--space-md);
`;

const WarningBox = styled(ErrorBox)`
  margin-bottom: var(--space-md);
  border-color: rgba(180, 83, 9, 0.3);
  background: rgba(180, 83, 9, 0.08);
  color: var(--color-warning-dark);
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: var(--space-md);
  margin: var(--space-xl) 0 var(--space-sm);
`;

const SectionTitle = styled.h2`
  margin: 0;
  color: var(--color-primary);
  font-size: var(--font-size-xl);
  font-family: var(--font-serif);
  font-weight: var(--font-weight-semibold);
`;

const SectionDescription = styled.p`
  margin: 4px 0 0;
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(33, 26, 18, 0.54);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-md);
  backdrop-filter: blur(4px);
`;

const Modal = styled.div`
  width: min(960px, 100%);
  max-height: 90vh;
  background: white;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  box-shadow: var(--shadow-large);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  padding: var(--space-md);
  border-bottom: 1px solid var(--color-border);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-md);
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: var(--color-text);
  font-size: var(--font-size-xl);
  font-family: var(--font-serif);
  font-weight: var(--font-weight-semibold);
`;

const CloseButton = styled.button`
  background: transparent;
  border: 0;
  color: var(--color-text-secondary);
  font-size: var(--font-size-2xl);
  line-height: 1;
  padding: var(--space-xs);
`;

const ModalContent = styled.div`
  overflow-y: auto;
  padding: var(--space-md);
  display: grid;
  gap: var(--space-md);
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-md);

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const DetailBlock = styled.div`
  border: 1px solid var(--color-border-light);
  background: var(--color-primary-lighter);
  border-radius: var(--border-radius-md);
  padding: var(--space-sm);
`;

const Label = styled.div`
  color: var(--color-text-secondary);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.14em;
  text-transform: uppercase;
  margin-bottom: 4px;
`;

const Value = styled.div`
  color: var(--color-text);
  font-size: var(--font-size-md);
  line-height: 1.55;
  overflow-wrap: anywhere;
`;

const MessageList = styled.div`
  display: grid;
  gap: var(--space-sm);
`;

const MessageItem = styled.div`
  border: 1px solid var(--color-border-light);
  border-radius: var(--border-radius-md);
  padding: var(--space-sm);
  background: ${props => props.sender === props.artistUid ? 'rgba(130, 172, 216, 0.1)' : 'white'};
`;

const MessageMeta = styled.div`
  display: flex;
  justify-content: space-between;
  gap: var(--space-sm);
  color: var(--color-text-secondary);
  font-size: var(--font-size-xs);
  margin-bottom: var(--space-xs);
`;

const MessageText = styled.div`
  color: var(--color-text);
  font-size: var(--font-size-sm);
  line-height: 1.6;
  white-space: pre-wrap;
`;

const ModalFooter = styled.div`
  padding: var(--space-md);
  border-top: 1px solid var(--color-border);
  background: var(--color-primary-lighter);
  display: flex;
  justify-content: space-between;
  gap: var(--space-md);
  flex-wrap: wrap;
`;

const SaveButton = styled.button`
  background: var(--color-periwinkle);
  color: white;
  border: 0;
  padding: var(--space-xs) var(--space-lg);
  border-radius: var(--border-radius-md);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-medium);

  &:hover {
    background: var(--color-accent);
  }
`;

const formatDate = (timestamp) => {
  if (!timestamp) return 'N/A';
  return new Date(timestamp).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const ReportDetailModal = ({ report, onClose, onUpdateStatus }) => {
  const [status, setStatus] = useState(report.status);
  const [updating, setUpdating] = useState(false);

  const handleSave = async () => {
    setUpdating(true);
    try {
      await onUpdateStatus(report.id, status);
      onClose();
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(event) => event.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>{report.artworkTitle}</ModalTitle>
          <CloseButton onClick={onClose} aria-label="Close report details">&times;</CloseButton>
        </ModalHeader>
        <ModalContent>
          <DetailGrid>
            <DetailBlock>
              <Label>Reason</Label>
              <Value>{REPORT_REASONS[report.reason] || REPORT_REASONS.other}</Value>
            </DetailBlock>
            <DetailBlock>
              <Label>Submitted</Label>
              <Value>{formatDate(report.createdAt)}</Value>
            </DetailBlock>
            <DetailBlock>
              <Label>Artist UID</Label>
              <Value>{report.artistUid}</Value>
            </DetailBlock>
            <DetailBlock>
              <Label>Collector UID</Label>
              <Value>{report.collectorUid}</Value>
            </DetailBlock>
            <DetailBlock>
              <Label>Conversation</Label>
              <Value>{report.conversationId}</Value>
            </DetailBlock>
            <DetailBlock>
              <Label>Block Scope</Label>
              <Value>{report.blockScope}</Value>
            </DetailBlock>
          </DetailGrid>

          <DetailBlock>
            <Label>Artist Notes</Label>
            <Value>{report.notes || 'No notes provided.'}</Value>
          </DetailBlock>

          <div>
            <Label>Captured Messages</Label>
            <MessageList>
              {report.messageContext.length === 0 ? (
                <EmptyState>No message context was captured.</EmptyState>
              ) : (
                report.messageContext.map((message) => (
                  <MessageItem key={message.id} sender={message.senderUid} artistUid={report.artistUid}>
                    <MessageMeta>
                      <span>{message.senderUid === report.artistUid ? 'Artist' : 'Collector'}</span>
                      <span>{formatDate(message.createdAt)}</span>
                    </MessageMeta>
                    <MessageText>{message.text}</MessageText>
                  </MessageItem>
                ))
              )}
            </MessageList>
          </div>
        </ModalContent>
        <ModalFooter>
          <SelectWrapper>
            <StatusSelect value={status} onChange={(event) => setStatus(event.target.value)}>
              {REPORT_STATUSES.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </StatusSelect>
          </SelectWrapper>
          <SaveButton onClick={handleSave} disabled={updating || status === report.status}>
            {updating ? 'Updating...' : 'Update Status'}
          </SaveButton>
        </ModalFooter>
      </Modal>
    </Overlay>
  );
};

const OffenceDetailModal = ({ offence, onClose, onUpdateStatus }) => {
  const [status, setStatus] = useState(offence.status);
  const [updating, setUpdating] = useState(false);

  const handleSave = async () => {
    setUpdating(true);
    try {
      await onUpdateStatus(offence.id, status);
      onClose();
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(event) => event.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Blocked message</ModalTitle>
          <CloseButton onClick={onClose} aria-label="Close offence details">&times;</CloseButton>
        </ModalHeader>
        <ModalContent>
          <DetailGrid>
            <DetailBlock>
              <Label>Detected</Label>
              <Value>{formatDate(offence.createdAt)}</Value>
            </DetailBlock>
            <DetailBlock>
              <Label>Source</Label>
              <Value>{offence.source.replace(/_/g, ' ')}</Value>
            </DetailBlock>
            <DetailBlock>
              <Label>Sender</Label>
              <Value>{offence.senderRole}: {offence.senderUid}</Value>
            </DetailBlock>
            <DetailBlock>
              <Label>Conversation</Label>
              <Value>{offence.conversationId || 'Not created'}</Value>
            </DetailBlock>
            <DetailBlock>
              <Label>Artist UID</Label>
              <Value>{offence.artistUid || 'N/A'}</Value>
            </DetailBlock>
            <DetailBlock>
              <Label>Collector UID</Label>
              <Value>{offence.collectorUid || 'N/A'}</Value>
            </DetailBlock>
            <DetailBlock>
              <Label>Artwork</Label>
              <Value>{offence.artworkTitle}</Value>
            </DetailBlock>
            <DetailBlock>
              <Label>Matched Categories</Label>
              <Value>{offence.categories.length ? offence.categories.join(', ') : 'None recorded'}</Value>
            </DetailBlock>
          </DetailGrid>

          <DetailBlock>
            <Label>Matched Terms</Label>
            <Value>
              {offence.matches.length
                ? offence.matches.map((match) => `${match.category}: ${match.term}`).join(', ')
                : 'No match metadata recorded.'}
            </Value>
          </DetailBlock>

          <DetailBlock>
            <Label>Blocked Message</Label>
            <MessageText>{offence.messageText || 'No message text recorded.'}</MessageText>
          </DetailBlock>
        </ModalContent>
        <ModalFooter>
          <SelectWrapper>
            <StatusSelect value={status} onChange={(event) => setStatus(event.target.value)}>
              {OFFENCE_STATUSES.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </StatusSelect>
          </SelectWrapper>
          <SaveButton onClick={handleSave} disabled={updating || status === offence.status}>
            {updating ? 'Updating...' : 'Update Status'}
          </SaveButton>
        </ModalFooter>
      </Modal>
    </Overlay>
  );
};

const ReportedConversations = () => {
  const [reports, setReports] = useState([]);
  const [offences, setOffences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [moderationError, setModerationError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedOffence, setSelectedOffence] = useState(null);

  const loadReports = async () => {
    setLoading(true);
    setError(null);
    setModerationError(null);
    try {
      const [reportsResult, offencesResult] = await Promise.allSettled([
        fetchReportedConversations({ status: statusFilter }),
        fetchMessageModerationOffences({ status: statusFilter })
      ]);

      if (reportsResult.status === 'rejected') {
        throw reportsResult.reason;
      }

      setReports(reportsResult.value);

      if (offencesResult.status === 'fulfilled') {
        setOffences(offencesResult.value);
      } else {
        console.error(offencesResult.reason);
        setOffences([]);
        setModerationError('Blocked messages could not be loaded. Collector reports are still available.');
      }
    } catch (err) {
      setReports([]);
      setOffences([]);
      setError('Failed to load reports. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const filteredReports = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return reports;

    return reports.filter((report) => (
      report.artworkTitle.toLowerCase().includes(term) ||
      report.reason.toLowerCase().includes(term) ||
      report.notes.toLowerCase().includes(term) ||
      report.artistUid.toLowerCase().includes(term) ||
      report.collectorUid.toLowerCase().includes(term) ||
      report.conversationId.toLowerCase().includes(term)
    ));
  }, [reports, searchTerm]);

  const filteredOffences = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return offences;

    return offences.filter((offence) => (
      offence.messageText.toLowerCase().includes(term) ||
      offence.artworkTitle.toLowerCase().includes(term) ||
      offence.senderUid.toLowerCase().includes(term) ||
      offence.senderRole.toLowerCase().includes(term) ||
      offence.artistUid.toLowerCase().includes(term) ||
      offence.collectorUid.toLowerCase().includes(term) ||
      offence.conversationId.toLowerCase().includes(term) ||
      offence.categories.join(' ').toLowerCase().includes(term) ||
      offence.matches.map((match) => `${match.category} ${match.term}`).join(' ').toLowerCase().includes(term)
    ));
  }, [offences, searchTerm]);

  const handleUpdateStatus = async (reportId, nextStatus) => {
    try {
      await updateReportedConversationStatus(reportId, nextStatus);
      setReports((current) =>
        current.map((report) => report.id === reportId ? { ...report, status: nextStatus } : report)
      );
    } catch (err) {
      console.error(err);
      alert('Failed to update report status.');
    }
  };

  const handleUpdateOffenceStatus = async (offenceId, nextStatus) => {
    try {
      await updateMessageModerationOffenceStatus(offenceId, nextStatus);
      setOffences((current) =>
        current.map((offence) => offence.id === offenceId ? { ...offence, status: nextStatus } : offence)
      );
    } catch (err) {
      console.error(err);
      alert('Failed to update offence status.');
    }
  };

  return (
    <PageContainer>
      <PageHeader>
        <Title>Reports & Moderation</Title>
        <Subtitle>Review artist-submitted collector reports and messages blocked by automated moderation.</Subtitle>
        <SummaryRow>
          <SummaryBadge>{reports.length} reports</SummaryBadge>
          <SummaryBadge>{offences.length} blocked messages</SummaryBadge>
          {(searchTerm.trim() || statusFilter !== 'all') && (
            <SummaryBadge>{filteredReports.length + filteredOffences.length} showing</SummaryBadge>
          )}
          <Button onClick={loadReports} disabled={loading}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
        </SummaryRow>
        <ControlsRow>
          <FiltersGroup>
            <SearchInput
              type="search"
              placeholder="Search artwork, reason, UID..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              aria-label="Search reported conversations"
            />
            <SelectWrapper>
              <StatusSelect
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                aria-label="Filter reports by status"
              >
                <option value="all">All Statuses</option>
                {REPORT_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </StatusSelect>
            </SelectWrapper>
          </FiltersGroup>
        </ControlsRow>
      </PageHeader>

      {moderationError && !error && (
        <WarningBox role="status">
          <span>{moderationError}</span>
        </WarningBox>
      )}

      {error ? (
        <ErrorBox role="alert">
          <span>{error}</span>
          <Button onClick={loadReports}>Try again</Button>
        </ErrorBox>
      ) : (
        <>
          <SectionHeader>
            <div>
              <SectionTitle>Collector Reports</SectionTitle>
              <SectionDescription>Artist-submitted reports with captured conversation context.</SectionDescription>
            </div>
          </SectionHeader>
          <TableWrapper aria-busy={loading}>
            {loading ? (
              <EmptyState>Loading reports...</EmptyState>
            ) : reports.length === 0 ? (
              <EmptyState>No reported conversations found.</EmptyState>
            ) : filteredReports.length === 0 ? (
              <EmptyState>No reports match your search or filter.</EmptyState>
            ) : (
              <ReportsTable>
                <thead>
                  <tr>
                    <TableHeadCell scope="col">Artwork</TableHeadCell>
                    <TableHeadCell scope="col">Reason</TableHeadCell>
                    <TableHeadCell scope="col">Participants</TableHeadCell>
                    <TableHeadCell scope="col">Submitted</TableHeadCell>
                    <TableHeadCell scope="col">Status</TableHeadCell>
                  </tr>
                </thead>
                <tbody>
                  {filteredReports.map((report) => (
                    <TableRow key={report.id}>
                      <TableCell>
                        <ReportButton onClick={() => setSelectedReport(report)}>
                          {report.artworkTitle}
                        </ReportButton>
                        <MetaText>{report.conversationId}</MetaText>
                      </TableCell>
                      <TableCell>{REPORT_REASONS[report.reason] || REPORT_REASONS.other}</TableCell>
                      <TableCell>
                        <div>Artist: {report.artistUid.slice(0, 10)}...</div>
                        <MetaText>Collector: {report.collectorUid.slice(0, 10)}...</MetaText>
                      </TableCell>
                      <TableCell>{formatDate(report.createdAt)}</TableCell>
                      <TableCell>
                        <StatusBadge status={report.status}>{report.status}</StatusBadge>
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </ReportsTable>
            )}
          </TableWrapper>

          <SectionHeader>
            <div>
              <SectionTitle>Blocked Messages</SectionTitle>
              <SectionDescription>Messages stopped by automated abuse and profanity checks.</SectionDescription>
            </div>
          </SectionHeader>
          <TableWrapper aria-busy={loading}>
            {loading ? (
              <EmptyState>Loading blocked messages...</EmptyState>
            ) : offences.length === 0 ? (
              <EmptyState>No blocked messages found.</EmptyState>
            ) : filteredOffences.length === 0 ? (
              <EmptyState>No blocked messages match your search or filter.</EmptyState>
            ) : (
              <ReportsTable>
                <thead>
                  <tr>
                    <TableHeadCell scope="col">Message</TableHeadCell>
                    <TableHeadCell scope="col">Matched</TableHeadCell>
                    <TableHeadCell scope="col">Sender</TableHeadCell>
                    <TableHeadCell scope="col">Detected</TableHeadCell>
                    <TableHeadCell scope="col">Status</TableHeadCell>
                  </tr>
                </thead>
                <tbody>
                  {filteredOffences.map((offence) => (
                    <TableRow key={offence.id}>
                      <TableCell>
                        <ReportButton onClick={() => setSelectedOffence(offence)}>
                          {offence.messageText || 'No message text recorded'}
                        </ReportButton>
                        <MetaText>{offence.artworkTitle}</MetaText>
                      </TableCell>
                      <TableCell>
                        {offence.categories.length ? offence.categories.join(', ') : 'Unknown'}
                        <MetaText>
                          {offence.matches.map((match) => match.term).join(', ') || 'No match metadata'}
                        </MetaText>
                      </TableCell>
                      <TableCell>
                        <div>{offence.senderRole}: {offence.senderUid.slice(0, 10)}...</div>
                        <MetaText>{offence.conversationId || offence.source.replace(/_/g, ' ')}</MetaText>
                      </TableCell>
                      <TableCell>{formatDate(offence.createdAt)}</TableCell>
                      <TableCell>
                        <StatusBadge status={offence.status}>{offence.status}</StatusBadge>
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </ReportsTable>
            )}
          </TableWrapper>
        </>
      )}

      {selectedReport && (
        <ReportDetailModal
          report={selectedReport}
          onClose={() => setSelectedReport(null)}
          onUpdateStatus={handleUpdateStatus}
        />
      )}
      {selectedOffence && (
        <OffenceDetailModal
          offence={selectedOffence}
          onClose={() => setSelectedOffence(null)}
          onUpdateStatus={handleUpdateOffenceStatus}
        />
      )}
    </PageContainer>
  );
};

export default ReportedConversations;
